// 📁 src/admin/ConfigPanel.js

import React, { useState, useEffect } from 'react';

export default function ConfigPanel() {
  const [basePrice, setBasePrice] = useState('');
  const [discountRate, setDiscountRate] = useState('');
  const [vatRate, setVatRate] = useState('');
  const [currency, setCurrency] = useState('BDT');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [exchangeRate, setExchangeRate] = useState('');
  const [annualDiscountRate, setAnnualDiscountRate] = useState('');
  const [allowOverride, setAllowOverride] = useState(true);

  const [modulePricingMap, setModulePricingMap] = useState({});

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [report, setReport] = useState([]);

  const [promoOccasion, setPromoOccasion] = useState('');
  const [promoDiscountRate, setPromoDiscountRate] = useState('');
  const [promoFromDate, setPromoFromDate] = useState('');
  const [promoToDate, setPromoToDate] = useState('');
  const [promoDurationMonths, setPromoDurationMonths] = useState('');

  useEffect(() => {
    const config = JSON.parse(localStorage.getItem('adminPricingConfig')) || {};
    if (config.basePrice !== undefined) setBasePrice(config.basePrice);
    if (config.discountRate !== undefined) setDiscountRate(config.discountRate);
    if (config.vatRate !== undefined) setVatRate(config.vatRate);
    if (config.currency !== undefined) setCurrency(config.currency);
    if (config.billingCycle !== undefined) setBillingCycle(config.billingCycle);
    if (config.exchangeRate !== undefined) setExchangeRate(config.exchangeRate);
    if (config.annualDiscountRate !== undefined) setAnnualDiscountRate(config.annualDiscountRate);
    if (config.allowOverride !== undefined) setAllowOverride(config.allowOverride);
    if (config.modulePricingMap !== undefined) setModulePricingMap(config.modulePricingMap);
  }, []);

  const handleSave = () => {
    const previousConfig = JSON.parse(localStorage.getItem('adminPricingConfig')) || {};
    const timestamp = new Date().toISOString();

    const fields = {
      basePrice,
      discountRate,
      vatRate,
      currency,
      billingCycle,
      exchangeRate,
      annualDiscountRate,
      allowOverride,
      modulePricingMap
    };

    const changes = [];
    Object.entries(fields).forEach(([key, value]) => {
      if (JSON.stringify(previousConfig[key]) !== JSON.stringify(value)) {
        changes.push({
          field: key,
          oldValue: previousConfig[key],
          newValue: value,
          timestamp
        });
      }
    });

    const existingLogs = JSON.parse(localStorage.getItem('pricingChangeLogs')) || [];
    localStorage.setItem('pricingChangeLogs', JSON.stringify([...existingLogs, ...changes]));

    const config = { ...fields, timestamp };
    localStorage.setItem('adminPricingConfig', JSON.stringify(config));
    alert('Configuration saved!');
  };

  const handleModulePriceChange = (module, value) => {
    setModulePricingMap((prev) => ({ ...prev, [module]: Number(value) }));
  };

  const handleExport = () => {
    const logs = JSON.parse(localStorage.getItem('pricingChangeLogs')) || [];
    const filtered = logs.filter((log) => {
      const ts = new Date(log.timestamp).getTime();
      const from = fromDate ? new Date(fromDate).getTime() : 0;
      const to = toDate ? new Date(toDate).getTime() : Infinity;
      return ts >= from && ts <= to;
    });
    setReport(filtered);
  };

  const handlePostPromo = () => {
    if (
      !promoOccasion ||
      !promoDiscountRate ||
      !promoFromDate ||
      !promoToDate ||
      !promoDurationMonths
    ) {
      alert('Please fill all promo fields before posting.');
      return;
    }

    const promo = {
      occasion: promoOccasion,
      discountRate: Number(promoDiscountRate),
      validFrom: promoFromDate,
      validTo: promoToDate,
      benefitDurationMonths: Number(promoDurationMonths),
      timestamp: new Date().toISOString()
    };

    localStorage.setItem('activePromo', JSON.stringify(promo));

    const existingLogs = JSON.parse(localStorage.getItem('promoChangeLogs')) || [];
    existingLogs.push({ ...promo, action: 'posted' });
    localStorage.setItem('promoChangeLogs', JSON.stringify(existingLogs));

    alert('Promo posted successfully!');
  };

  return (
    <div className="p-6 max-w-2xl mx-auto bg-white rounded shadow">
      <h1 className="text-2xl font-bold text-indigo-700 mb-4">Configuration Panel</h1>
      <p className="text-gray-700 mb-6">
        Manage system-wide pricing, currency, billing, and promotional settings.
      </p>

      <div className="space-y-4 mt-8">
        <label className="block">
          Subscription Fee (default fallback)
          <input
            type="number"
            value={basePrice}
            onChange={(e) => setBasePrice(Number(e.target.value))}
            placeholder="Enter fallback subscription fee"
            className="mt-1 w-full border rounded px-3 py-2"
          />
        </label>

        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700">Module-Specific Pricing</h3>
          {['ATS', 'Scout', 'PeopleSync', 'AuditReady', 'PerformEdge'].map((module) => (
            <label key={module} className="block">
              {module} Subscription Fee
              <input
                type="number"
                value={modulePricingMap[module] || ''}
                onChange={(e) => handleModulePriceChange(module, e.target.value)}
                placeholder={`Enter ${module} fee`}
                className="mt-1 w-full border rounded px-3 py-2"
              />
            </label>
          ))}
        </div>

        <label className="block">
          Discount Rate (%)
          <input
            type="number"
            value={discountRate}
            onChange={(e) => setDiscountRate(Number(e.target.value))}
            placeholder="Enter default discount rate"
            className="mt-1 w-full border rounded px-3 py-2"
          />
        </label>

        <label className="block">
          VAT Rate (%)
          <input
            type="number"
            value={vatRate}
            onChange={(e) => setVatRate(Number(e.target.value))}
            placeholder="Enter VAT percentage"
            className="mt-1 w-full border rounded px-3 py-2"
          />
        </label>

        <label className="block">
          Currency
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="mt-1 w-full border rounded px-3 py-2"
          >
            <option value="BDT">BDT</option>
            <option value="USD">USD</option>
          </select>
        </label>

        <label className="block">
          Billing Cycle
          <select
            value={billingCycle}
            onChange={(e) => setBillingCycle(e.target.value)}
            className="mt-1 w-full border rounded px-3 py-2"
          >
            <option value="monthly">Monthly</option>
            <option value="annual">Annual</option>
          </select>
        </label>

        <label className="block">
          Exchange Rate (USD → BDT)
          <input
            type="number"
            step="0.0001"
            value={exchangeRate}
            onChange={(e) => setExchangeRate(Number(e.target.value))}
            placeholder="Enter conversion rate"
            className="mt-1 w-full border rounded px-3 py-2"
          />
        </label>

        <label className="block">
          Annual Discount Rate (%)
          <input
            type="number"
            value={annualDiscountRate}
            onChange={(e) => setAnnualDiscountRate(Number(e.target.value))}
            placeholder="Enter annual discount rate"
            className="mt-1 w-full border rounded px-3 py-2"
          />
        </label>

        <label className="block flex items-center gap-2">
          <input
            type="checkbox"
            checked={allowOverride}
            onChange={(e) => setAllowOverride(e.target.checked)}
          />
          Allow Client Override (Currency & Billing Cycle)
        </label>

        <button
          onClick={handleSave}
          className="mt-6 w-full bg-indigo-600 text-white py-2 rounded hover:bg-indigo-700"
        >
          Save Configuration
        </button>
      </div>

      <div className="mt-10 border-t pt-6">
        <h2 className="text-xl font-semibold text-indigo-700 mb-4">Occasion-Based Promo</h2>

        <label className="block mb-2">
          Occasion Name
          <input
            type="text"
            value={promoOccasion}
            onChange={(e) => setPromoOccasion(e.target.value)}
            placeholder="Enter occasion name"
            className="mt-1 w-full border rounded px-3 py-2"
          />
        </label>

        <label className="block mb-2">
          Discount Rate (%)
          <input
            type="number"
            value={promoDiscountRate}
            onChange={(e) => setPromoDiscountRate(Number(e.target.value))}
            placeholder="Enter promo discount rate"
            className="mt-1 w-full border rounded px-3 py-2"
          />
        </label>

        <div className="flex gap-4 mb-2">
          <label className="block w-full">
            Valid From
            <input
              type="date"
              value={promoFromDate}
              onChange={(e) => setPromoFromDate(e.target.value)}
              className="mt-1 w-full border rounded px-3 py-2"
            />
          </label>
          <label className="block w-full">
            Valid To
            <input
              type="date"
              value={promoToDate}
              onChange={(e) => setPromoToDate(e.target.value)}
              className="mt-1 w-full border rounded px-3 py-2"
            />
          </label>
        </div>

        <label className="block mb-4">
          Benefit Duration (months)
          <input
            type="number"
            value={promoDurationMonths}
            onChange={(e) => setPromoDurationMonths(Number(e.target.value))}
            placeholder="Enter duration in months"
            className="mt-1 w-full border rounded px-3 py-2"
          />
        </label>

        <button
          onClick={handlePostPromo}
          className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
        >
          Post Promo
        </button>
      </div>
    </div>
  );
}
